import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useAudioPlayer } from "@/components/AudioPlayerProvider";

interface AudioPlayerProps {
  beatTitle: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  onSeek: (time: number) => void;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
}

export const AudioPlayer = ({ 
  beatTitle, 
  isPlaying, 
  onPlayPause,
  currentTime,
  duration,
  volume,
  isMuted,
  onSeek,
  onVolumeChange,
  onMuteToggle
}: AudioPlayerProps) => {
  const { isModalOpen } = useAudioPlayer();

  const handleSeek = (value: number[]) => {
    if (duration > 0 && isModalOpen) {
      const newTime = (value[0] / 100) * duration;
      onSeek(newTime);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    onVolumeChange(value[0]);
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="bg-card border rounded-lg p-4 space-y-4">
      {/* Play/Pause and Title */}
      <div className="flex items-center gap-3">
        <Button
          onClick={onPlayPause}
          size="sm"
          className="rounded-full w-10 h-10"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4 ml-0.5" />
          )}
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{beatTitle}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <Slider
          value={[progressPercentage]}
          onValueChange={handleSeek}
          max={100}
          min={0}
          step={0.1}
          className="w-full slider-gradient cursor-pointer"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Volume Control */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onMuteToggle}
          className="p-0 h-auto w-auto"
        >
          {isMuted || volume === 0 ? (
            <VolumeX className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Volume2 className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
        <Slider
          value={[volume]}
          onValueChange={handleVolumeChange}
          max={100}
          min={0}
          step={1}
          className="flex-1 slider-gradient"
        />
        <span className="text-xs text-muted-foreground w-8">{Math.round(volume)}%</span>
      </div>
    </div>
  );
};